import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import useMediaQuery from "@mui/material/useMediaQuery";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import EventIcon from "@mui/icons-material/Event";
import {
  Grid,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Snackbar,
  Alert,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Menu,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Skeleton from "@mui/material/Skeleton";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { useTheme } from "@mui/material/styles";
import "./dashboard.css";
import { PageLayout } from "../../ui-components/PageLayout";
import YearCard from "../../ui-components/Dashboard/YearCard";
import PipelineCard from "../../ui-components/Dashboard/PipelineCard";
import {
  PIPELINE_ROUTE,
  usePipelineFolder,
} from "../../hooks/usePipeline";
import DeleteConfirmationDialog from "../../ui-components/Dialogs/DeleteConfirmationDialog";
import { Dayjs } from "dayjs";
import { RootState } from "../../store/reducers";
import {
  createTransactionYear,
  fetchTransactionYears,
  deleteTransactionYear,
} from "../../store/modules/transactionYear/transactionYearActions";
import { AppDispatch } from "../../store/store";
import { useQuery } from "@tanstack/react-query";
import TransactionSummaryContainer from "../../ui-components/Dashboard/TransactionSummaryContainer";
import { GciTrackerContainer } from "../../ui-components/Dashboard/GciTrackerContainer";
import { ITransactionYearModel } from "../../types/types";

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { loading, years, created, createError, deleted, deleteError } =
    useSelector((state: RootState) => state.transactionYear);
  const { selectedCoAgentIds } = useSelector(
    (state: RootState) => state.coAgentFilter,
  );
  const { data: pipelineFolder } = usePipelineFolder();
  const showPipeline = !!pipelineFolder?.id;

  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [yearToDelete, setYearToDelete] = useState<{
    id: number;
    year: number;
  } | null>(null);
  const [newYearDate, setNewYearDate] = useState<Dayjs | null>(null);
  const [viewMode, setViewMode] = useState<"cards" | "table">("cards");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [gridMenuAnchorEl, setGridMenuAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [selectedYearId, setSelectedYearId] = useState<number | null>(null);
  const isGridMenuOpen = Boolean(gridMenuAnchorEl);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  useQuery({
    queryKey: ["fetchTransactionYears", selectedCoAgentIds],
    queryFn: () => {
      dispatch(fetchTransactionYears());
      return Promise.resolve(null);
    },
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (created) {
      setSnackbar({
        open: true,
        message: "Year created successfully",
        severity: "success",
      });
    }
  }, [created]);
  useEffect(() => {
    if (createError) {
      setSnackbar({
        open: true,
        message: createError,
        severity: "error",
      });
    }
  }, [createError]);

  useEffect(() => {
    if (deleted) {
      setSnackbar({
        open: true,
        message: "Year deleted successfully",
        severity: "success",
      });
    }
  }, [deleted]);

  useEffect(() => {
    if (deleteError) {
      setSnackbar({
        open: true,
        message: `Error deleting year: ${deleteError}`,
        severity: "error",
      });
    }
  }, [deleteError]);

  const handleAddYear = async () => {
    if (newYearDate) {
      dispatch(createTransactionYear(newYearDate.year()));
    }
    setOpen(false);
    setNewYearDate(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const sortYears = (
    years: ITransactionYearModel[],
    sortOrder: "asc" | "desc",
  ) => {
    return [...years].sort((a, b) => {
      const yearA = a.year || 0;
      const yearB = b.year || 0;
      return sortOrder === "desc" ? yearB - yearA : yearA - yearB;
    });
  };

  const handleDeleteYear = (id: number, year: number) => {
    setYearToDelete({ id, year });
    setDeleteDialogOpen(true);
  };

  const confirmDeleteYear = () => {
    if (yearToDelete) {
      dispatch(deleteTransactionYear(yearToDelete.id));
    }
    setDeleteDialogOpen(false);
    setYearToDelete(null);
  };

  const handleGridMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    id: number,
  ) => {
    event.stopPropagation();
    setGridMenuAnchorEl(event.currentTarget);
    setSelectedYearId(id);
  };

  const handleGridMenuClose = () => {
    setGridMenuAnchorEl(null);
    setSelectedYearId(null);
  };

  const handleGridDelete = () => {
    handleGridMenuClose();
    if (selectedYearId !== null) {
      const yearData = years.find((y) => y.id === selectedYearId);
      if (yearData) {
        handleDeleteYear(selectedYearId, yearData.year || 0);
      }
    }
  };

  return (
    <PageLayout>
      <Box sx={{ mb: 6, ml: isMobile ? 0 : 1 }}>
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h4"
            color="primary"
            component="h1"
            fontWeight="bold"
          >
            Dashboard
          </Typography>
          <Box
            display="flex"
            justifyContent="space-between"
            mt={2}
            flexWrap={isMobile ? "wrap" : "nowrap"}
          >
            <Box
              display="flex"
              alignItems="center"
              mb={isMobile ? 2 : 0}
              width={isMobile ? "100%" : "auto"}
              gap={2}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleClickOpen}
                startIcon={<AddIcon />}
                size={isMobile ? "small" : "medium"}
              >
                Year
              </Button>

              <Button
                startIcon={
                  sortOrder === "desc" ? (
                    <ArrowDownwardIcon />
                  ) : (
                    <ArrowUpwardIcon />
                  )
                }
                onClick={() =>
                  setSortOrder(sortOrder === "desc" ? "asc" : "desc")
                }
                color="primary"
                size="small"
              >
                Sort {sortOrder === "desc" ? "Newest First" : "Oldest First"}
              </Button>
            </Box>

            {/* View Controls: View Mode Toggle */}
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(
                  event: React.MouseEvent<HTMLElement>,
                  newMode: "cards" | "table",
                ) => {
                  if (newMode) setViewMode(newMode);
                }}
                size="small"
              >
                <ToggleButton value="cards" aria-label="Cards view">
                  <ViewModuleIcon />
                </ToggleButton>
                <ToggleButton value="table" aria-label="Table view">
                  <ViewListIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          {/* Statistics row */}
          {/* <Box display="flex" flexWrap="wrap" mt={2} gap={2}>
            <Chip
              icon={<EventIcon />}
              label={`${years.length} Years`}
              color="primary"
              variant="outlined"
            />
            <Chip
              icon={<AssignmentIcon />}
              label={`${years.reduce(
                (sum, year) => sum + (year.transactionCount || 0),
                0
              )} Transactions`}
              color="secondary"
              variant="outlined"
            />
            <Chip
              icon={<NewReleasesIcon />}
              label={`Latest: ${
                years.length > 0 && years.some((y) => y.year !== undefined)
                  ? Math.max(...years.map((y) => y.year || 0))
                  : "N/A"
              }`}
              color="info"
              variant="outlined"
            />
          </Box> */}
        </Box>

        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>Add Year</DialogTitle>
          <DialogContent>
            <DatePicker
              views={["year"]}
              value={newYearDate}
              onChange={(newValue) => setNewYearDate(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: "dense",
                  label: "Select Year",
                },
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary">
              Cancel
            </Button>
            <Button onClick={handleAddYear} color="primary">
              Add
            </Button>
          </DialogActions>
        </Dialog>

        <Grid
          container
          p={isMobile ? 1 : 2}
          pt={0}
          spacing={isMobile ? 1 : 2}
          my={isMobile ? 1 : 2}
        >
          {!loading && (
            <Grid item xs={12}>
              <GciTrackerContainer />
            </Grid>
          )}

          {!loading && (
            <Grid item xs={12}>
              <TransactionSummaryContainer />
            </Grid>
          )}

          {loading ? (
            Array.from(new Array(3)).map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={`skeleton-${index}`}>
                <Skeleton
                  variant="rounded"
                  height={180}
                  animation="wave"
                  sx={{ borderRadius: 2 }}
                />
              </Grid>
            ))
          ) : viewMode === "table" ? (
            <Box sx={{ width: "100%", mt: 2 }}>
              <DataGrid
                density="compact"
                disableColumnSelector
                disableColumnMenu
                // The Pipeline row stays first whatever the Newest/Oldest order
                rows={[
                  ...(showPipeline
                    ? [
                        {
                          id: pipelineFolder!.id,
                          year: "Pipeline",
                          transactionCount: pipelineFolder!.entryCount,
                          isPipeline: true,
                        },
                      ]
                    : []),
                  ...sortYears(years, sortOrder).map((y) => ({
                    ...y,
                    isPipeline: false,
                  })),
                ]}
                onRowClick={(params) => {
                  navigate(
                    `/transactions/${params.row.isPipeline ? PIPELINE_ROUTE : params.row.year}`,
                  );
                }}
                sx={{
                  "& .MuiDataGrid-row": {
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  },
                  minHeight: 300,
                  border: 1,
                  borderColor: "divider",
                  "& .MuiDataGrid-cell:focus": {
                    outline: "none",
                  },
                }}
                columns={[
                  {
                    field: "year",
                    headerName: "Year",
                    width: 150,
                    sortable: true,
                  },
                  {
                    field: "transactionCount",
                    headerName: "Transactions",
                    width: 200,
                    renderCell: (params) => (
                      <Box display="flex" alignItems="center">
                        <Typography>{params.value || 0}</Typography>
                      </Box>
                    ),
                  },
                  {
                    field: "actions",
                    headerName: "Actions",
                    width: 100,
                    sortable: false,
                    renderCell: (params) => {
                      if (params.row.isPipeline) return null;
                      const hasTransactions =
                        (params.row.transactionCount || 0) > 0;
                      const isFilterActive = selectedCoAgentIds.length > 0;
                      const canDelete = !hasTransactions && !isFilterActive;
                      return canDelete ? (
                        <IconButton
                          size="small"
                          onClick={(e) =>
                            handleGridMenuOpen(e, params.row.id || 0)
                          }
                          color="inherit"
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      ) : null;
                    },
                  },
                  // {
                  //   field: "createdAt",
                  //   headerName: "Created Date",
                  //   width: 200,
                  //   valueFormatter: (params: {
                  //     value: string | number | Date;
                  //   }) => new Date(params.value).toLocaleDateString(),
                  // },
                ]}
                pageSizeOptions={[10, 20, 50]}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 20, page: 0 },
                  },
                }}
                pagination
                disableRowSelectionOnClick
              />
            </Box>
          ) : (
            <>
              {years.length === 0 && !loading && (
                <Grid item xs={12}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 4,
                      textAlign: "center",
                      borderRadius: 2,
                      bgcolor: "background.paper",
                    }}
                  >
                    <Box sx={{ mb: 2 }}>
                      <EventIcon
                        sx={{
                          fontSize: 60,
                          color: "text.secondary",
                          opacity: 0.7,
                        }}
                      />
                    </Box>
                    <Typography variant="h5" gutterBottom>
                      No Transaction Years
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      paragraph
                    >
                      You haven't added any transaction years yet. Add your
                      first year to get started.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleClickOpen}
                      startIcon={<AddIcon />}
                      size="large"
                    >
                      Year
                    </Button>
                  </Paper>
                </Grid>
              )}
              {showPipeline && (
                <Grid item xs={12} sm={6} md={4} key="pipeline">
                  <PipelineCard
                    entryCount={pipelineFolder!.entryCount || 0}
                    overdueCount={pipelineFolder!.overdueCount || 0}
                  />
                </Grid>
              )}
              {sortYears(years, sortOrder).map((data) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={data.year || `unknown-${Math.random()}`}
                >
                  {data.year !== undefined && (
                    <YearCard
                      yearId={data.id!}
                      year={data.year}
                      transactionCount={data.transactionCount || 0}
                      onDelete={
                        (data.transactionCount || 0) === 0 &&
                        selectedCoAgentIds.length === 0
                          ? () => handleDeleteYear(data.id || 0, data.year || 0)
                          : undefined
                      }
                    />
                  )}
                </Grid>
              ))}
            </>
          )}
        </Grid>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDeleteYear}
        optionName={`${yearToDelete?.year}`}
      />

      <Menu
        anchorEl={gridMenuAnchorEl}
        open={isGridMenuOpen}
        onClose={handleGridMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleGridDelete} sx={{ color: "error.main" }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* <TransactionSearchDialog
        open={searchDialogOpen}
        onClose={() => setSearchDialogOpen(false)}
      /> */}
    </PageLayout>
  );
};

export default Dashboard;
